import { useMemo } from 'react';
import { Card, Empty, Row, Col, Statistic, Typography } from 'antd';
import { Pie } from '@ant-design/charts';
import { EnvironmentOutlined, AreaChartOutlined, AimOutlined } from '@ant-design/icons';
import { primary, neutral } from '@/theme';

const { Text } = Typography;

interface CommuneCoverageStatsProps {
  features: Array<{
    properties: {
      id: string;
      code: string;
      name: string;
      areaKm2: number;
    };
  }>;
}

const PALETTE = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#6B7280',
];

export function CommuneCoverageStats({ features }: CommuneCoverageStatsProps) {
  const stats = useMemo(() => {
    if (!features.length) return null;

    const sorted = [...features].sort(
      (a, b) => b.properties.areaKm2 - a.properties.areaKm2,
    );

    const totalArea = sorted.reduce((s, f) => s + f.properties.areaKm2, 0);
    const top5 = sorted.slice(0, 5);
    const othersArea = sorted.slice(5).reduce((s, f) => s + f.properties.areaKm2, 0);

    const pieData = top5.map((f) => ({
      type: f.properties.name,
      value: f.properties.areaKm2,
      pct: ((f.properties.areaKm2 / totalArea) * 100).toFixed(1),
    }));
    if (othersArea > 0) {
      pieData.push({
        type: 'Autres',
        value: othersArea,
        pct: ((othersArea / totalArea) * 100).toFixed(1),
      });
    }

    return { totalArea, communeCount: features.length, pieData, largest: sorted[0]! };
  }, [features]);

  if (!stats) {
    return <Empty description="Sélectionnez des communes" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div>
      <Row gutter={12} style={{ marginBottom: 20 }}>
        <Col span={8}>
          <Card
            size="small"
            bordered={false}
            style={{
              background: primary[50],
              borderRadius: 10,
              textAlign: 'center',
            }}
            bodyStyle={{ padding: '14px 12px' }}
          >
            <Statistic
              title={<span style={{ fontSize: 11, color: neutral[500] }}>Communes</span>}
              value={stats.communeCount}
              prefix={<EnvironmentOutlined style={{ fontSize: 14, color: primary[400] }} />}
              valueStyle={{ fontSize: 22, fontWeight: 700, color: primary[600] }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            size="small"
            bordered={false}
            style={{
              background: '#F0FDF4',
              borderRadius: 10,
              textAlign: 'center',
            }}
            bodyStyle={{ padding: '14px 12px' }}
          >
            <Statistic
              title={<span style={{ fontSize: 11, color: neutral[500] }}>Superficie totale</span>}
              value={stats.totalArea}
              suffix="km²"
              precision={1}
              prefix={<AreaChartOutlined style={{ fontSize: 14, color: '#10B981' }} />}
              valueStyle={{ fontSize: 22, fontWeight: 700, color: '#065F46' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card
            size="small"
            bordered={false}
            style={{
              background: '#FEF3C7',
              borderRadius: 10,
              textAlign: 'center',
            }}
            bodyStyle={{ padding: '14px 12px' }}
          >
            <Statistic
              title={<span style={{ fontSize: 11, color: neutral[500] }}>Plus grande</span>}
              value={stats.largest.properties.name}
              suffix={
                <span style={{ fontSize: 12, fontWeight: 400, color: neutral[500] }}>
                  ({stats.largest.properties.areaKm2} km²)
                </span>
              }
              prefix={<AimOutlined style={{ fontSize: 14, color: '#F59E0B' }} />}
              valueStyle={{ fontSize: 16, fontWeight: 700, color: '#92400E' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        bordered={false}
        title={
          <span style={{ fontSize: 13, fontWeight: 600, color: neutral[700] }}>
            Répartition de la superficie
          </span>
        }
        style={{ borderRadius: 10, border: `1px solid ${neutral[100]}` }}
        bodyStyle={{ padding: '12px 16px 8px' }}
      >
        <div style={{ position: 'relative' }}>
          <Pie
            data={stats.pieData}
            angleField="value"
            colorField="type"
            radius={0.92}
            innerRadius={0.62}
            color={PALETTE}
            label={{
              text: (d: { type: string; value: number; pct: string }) =>
                `${d.type}\n${d.value.toLocaleString('fr-FR')} km² · ${d.pct}%`,
              style: {
                fontSize: 11,
                fontWeight: 500,
                textAlign: 'center',
                fill: neutral[700],
              },
              position: 'outside',
              connectorStroke: neutral[200],
              connectorLineWidth: 1,
            }}
            legend={false}
            style={{ stroke: '#fff', lineWidth: 2 }}
            height={280}
            tooltip={{
              title: 'type',
              items: [
                {
                  field: 'value',
                  name: 'Superficie',
                  valueFormatter: (v: number) => `${v.toLocaleString('fr-FR')} km²`,
                },
              ],
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 700, color: neutral[700], lineHeight: 1.2 }}>
              {stats.totalArea.toLocaleString('fr-FR')}
            </div>
            <div style={{ fontSize: 11, color: neutral[500], marginTop: 2 }}>
              km² au total
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 16px',
            justifyContent: 'center',
            marginTop: 8,
            paddingTop: 10,
            borderTop: `1px solid ${neutral[100]}`,
          }}
        >
          {stats.pieData.map((d, i) => (
            <div key={d.type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: PALETTE[i],
                  flexShrink: 0,
                }}
              />
              <Text style={{ fontSize: 11, color: neutral[500] }}>
                {d.type}{' '}
                <span style={{ fontWeight: 600, color: neutral[700] }}>
                  {d.value.toLocaleString('fr-FR')} km²
                </span>
              </Text>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
